import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { CircularProgress, Container, Typography, Box, Grid, Card, CardContent, Button, TextField, AppBar, Toolbar, Accordion, AccordionSummary, AccordionDetails } from "@material-ui/core";
import { CheckCircle, Star, ExpandMore } from "@material-ui/icons";
import api from "../../services/api";
import LeadCaptureForm from "../../components/LeadCaptureForm";
import QuizForm from "../../components/QuizForm";
import { captureUTMParams, storeUTMParams } from "../../utils/utmCapture";
import { getBackendUrl } from "../../config";

const PublicLandingPage = () => {
    const { slug } = useParams();
    const [loading, setLoading] = useState(true);
    const [landingPage, setLandingPage] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Capturar parâmetros UTM da URL
        const utmParams = captureUTMParams();
        storeUTMParams(utmParams);

        const fetchLandingPage = async () => {
            try {
                const { data } = await api.get(`/lp/${slug}`);
                setLandingPage(data);
            } catch (err) {
                setError("Landing page não encontrada");
            } finally {
                setLoading(false);
            }
        };

        fetchLandingPage();
    }, [slug]);

    // Helper function to build URL with UTM parameters
    const buildUrlWithUTMs = (baseUrl) => {
        const urlParams = new URLSearchParams(window.location.search);
        const utmParams = new URLSearchParams();

        // Copy all UTM parameters
        ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid'].forEach(param => {
            if (urlParams.has(param)) {
                utmParams.set(param, urlParams.get(param));
            }
        });

        // If baseUrl already has query params, append with &, otherwise with ?
        const separator = baseUrl.includes('?') ? '&' : '?';
        const utmString = utmParams.toString();

        return utmString ? `${baseUrl}${separator}${utmString}` : baseUrl;
    };

    // Remove the body style effect as we will handle scroll internally
    // useEffect(() => {
    //     document.body.style.overflow = "auto";
    //     ...
    // }, []);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Container maxWidth="md">
                <Box textAlign="center" mt={10}>
                    <Typography variant="h4" color="error">{error}</Typography>
                </Box>
            </Container>
        );
    }

    const primaryColor = landingPage.primaryColor || "#6366f1";
    const secondaryColor = landingPage.secondaryColor || "#8b5cf6";
    const isImmersive = landingPage.template === 'immersive';
    const headerColor = isImmersive ? "white" : primaryColor;

    const getImageUrl = (url) => {
        if (!url) return null;
        let cleanUrl = url;
        if (cleanUrl.includes(",")) {
            cleanUrl = cleanUrl.split(",")[0].trim();
        }
        if (cleanUrl.startsWith("http") || cleanUrl.startsWith("//")) return cleanUrl;
        const backendUrl = getBackendUrl();
        return `${backendUrl}/public/${cleanUrl}`;
    };

    const scrollToSection = (sectionId) => {
        const section = document.getElementById(sectionId);
        if (section) {
            section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const safeParse = (data) => {
        if (!data) return [];
        if (Array.isArray(data)) return data;
        try {
            const parsed = JSON.parse(data);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return [];
        }
    };

    const benefits = safeParse(landingPage.benefits);
    const steps = safeParse(landingPage.steps);
    const testimonials = safeParse(landingPage.testimonials);
    const plans = safeParse(landingPage.plans);
    const faq = safeParse(landingPage.faq);
    const quizQuestions = safeParse(landingPage.quizQuestions);

    return (
        <div
            style={{
                fontFamily: landingPage.fontFamily || "Inter, sans-serif",
                backgroundColor: "#fff",
                height: "100vh",
                width: "100vw",
                overflowY: "auto",
                overflowX: "hidden",
                position: "fixed", // Force it to sit on top of everything
                top: 0,
                left: 0,
                zIndex: 9999 // Ensure it's above other layers
            }}
        >
            {/* Header */}
            <AppBar
                position={isImmersive ? "absolute" : "sticky"}
                style={{
                    backgroundColor: isImmersive ? "transparent" : "white",
                    boxShadow: isImmersive ? "none" : "0 2px 10px rgba(0,0,0,0.05)",
                    top: 0,
                    zIndex: 10
                }}
            >
                <Toolbar>
                    {landingPage.headerLogo ? (
                        <img
                            src={getImageUrl(landingPage.headerLogo)}
                            alt={landingPage.name}
                            style={{ height: 40, maxWidth: 200, objectFit: "contain", cursor: "pointer" }}
                            onClick={() => scrollToSection('home')}
                        />
                    ) : (
                        <Typography variant="h6" style={{ color: headerColor, fontWeight: 700, cursor: "pointer" }} onClick={() => scrollToSection('home')}>
                            {landingPage.name || "Logo"}
                        </Typography>
                    )}
                    <Box flexGrow={1} />

                    <Button style={{ color: headerColor, marginRight: 10 }} onClick={() => scrollToSection('home')}>Início</Button>
                    {benefits.length > 0 && (
                        <Button style={{ color: headerColor, marginRight: 10 }} onClick={() => scrollToSection('features')}>Recursos</Button>
                    )}
                    {steps.length > 0 && (
                        <Button style={{ color: headerColor, marginRight: 10 }} onClick={() => scrollToSection('how-it-works')}>Como Funciona</Button>
                    )}
                    {testimonials.length > 0 && (
                        <Button style={{ color: headerColor, marginRight: 10 }} onClick={() => scrollToSection('testimonials')}>Depoimentos</Button>
                    )}
                    {plans.length > 0 && (
                        <Button style={{ color: headerColor, marginRight: 10 }} onClick={() => scrollToSection('pricing')}>Preços</Button>
                    )}
                    {faq.length > 0 && (
                        <Button style={{ color: headerColor, marginRight: 10 }} onClick={() => scrollToSection('faq')}>FAQ</Button>
                    )}

                    <Button
                        variant="contained"
                        onClick={() => {
                            if (landingPage.sourceType === 'pwa') {
                                window.location.href = buildUrlWithUTMs(`/pwa?id_campanha=${landingPage.id}`);
                            } else {
                                window.location.href = buildUrlWithUTMs(`/lp/${landingPage.slug}/quiz`);
                            }
                        }}
                        style={{ backgroundColor: primaryColor, color: "white", borderRadius: 20, padding: "8px 25px" }}
                    >
                        {landingPage.buttonText || "Começar"}
                    </Button>
                </Toolbar>
            </AppBar>

            {/* Hero Section */}
            {isImmersive ? (
                <Box
                    id="home"
                    style={{
                        position: "relative",
                        minHeight: "100vh",
                        display: "flex",
                        alignItems: "center",
                        backgroundImage: `url("${getImageUrl(landingPage.heroBackground || landingPage.heroImage) || "https://via.placeholder.com/1600x900/1e293b/ffffff?text=Hero+Background"}")`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        color: "white"
                    }}
                >
                    {/* Overlay removido a pedido do usuário */}
                    <Container maxWidth="lg" style={{ position: "relative", zIndex: 2 }}>
                        <Grid container spacing={6} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <Typography variant="overline" style={{ color: secondaryColor, fontWeight: 700, letterSpacing: 2, fontSize: "1rem" }}>
                                    SOLUÇÃO COMPLETA
                                </Typography>
                                <Typography variant="h2" style={{ fontWeight: 800, marginTop: 10, marginBottom: 20, lineHeight: 1.1, color: landingPage.titleColor || (isImmersive ? "white" : "inherit") }}>
                                    {landingPage.title}
                                </Typography>
                                <Typography variant="h5" style={{ color: "rgba(255,255,255,0.9)", marginBottom: 40, lineHeight: 1.6, maxWidth: "90%" }}>
                                    {landingPage.subtitle || "Transforme sua forma de trabalhar com nossa plataforma inovadora"}
                                </Typography>
                                <Box display="flex" gap={2} flexWrap="wrap">
                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={() => {
                                            if (landingPage.sourceType === 'pwa') {
                                                window.location.href = buildUrlWithUTMs(`/pwa?id_campanha=${landingPage.id}`);
                                            } else if (quizQuestions.length > 0) {
                                                window.location.href = buildUrlWithUTMs(`/lp/${landingPage.slug}/quiz`);
                                            } else {
                                                document.getElementById('lead-capture-btn')?.click();
                                            }
                                        }}
                                        style={{ backgroundColor: primaryColor, color: "white", padding: "15px 40px", borderRadius: 30, textTransform: "none", fontSize: 18, fontWeight: 700, boxShadow: `0 10px 20px ${primaryColor}66` }}
                                    >
                                        {landingPage.buttonText || "Começar Gratuitamente"}
                                    </Button>

                                    {/* Hidden button for LeadCaptureForm */}
                                    <div style={{ display: 'none' }}>
                                        <LeadCaptureForm
                                            landingPageId={landingPage.id}
                                            primaryColor={primaryColor}
                                            buttonText="Abrir"
                                            ref={(el) => {
                                                if (el) {
                                                    const btn = el.querySelector('button');
                                                    if (btn) btn.id = 'lead-capture-btn';
                                                }
                                            }}
                                        />
                                    </div>
                                </Box>
                                <Box mt={5} display="flex" alignItems="center" gap={3}>
                                    <Box display="flex" alignItems="center">
                                        {[1, 2, 3, 4, 5].map(i => <Star key={i} style={{ color: "#fbbf24", fontSize: 24 }} />)}
                                    </Box>
                                    <Typography variant="body1" style={{ color: "rgba(255,255,255,0.9)" }}>
                                        4.9/5 de 2.000+ avaliações
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box style={{ position: "relative", borderRadius: 20, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.15)", transform: "perspective(1000px) rotateY(-5deg)" }}>
                                    <img
                                        src={getImageUrl(landingPage.heroImage) || "https://via.placeholder.com/600x400/6366f1/ffffff?text=Hero+Image"}
                                        alt="Hero"
                                        style={{ width: "100%", display: "block" }}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Container>
                </Box>
            ) : (
                <Container maxWidth="lg" style={{ padding: "80px 20px" }} id="home">
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Typography variant="overline" style={{ color: primaryColor, fontWeight: 600, letterSpacing: 2 }}>
                                SOLUÇÃO COMPLETA
                            </Typography>
                            <Typography variant="h2" style={{ fontWeight: 800, marginTop: 10, marginBottom: 20, lineHeight: 1.2, color: landingPage.titleColor || "inherit" }}>
                                {landingPage.title}
                            </Typography>
                            <Typography variant="h6" style={{ color: "#64748b", marginBottom: 30, lineHeight: 1.6 }}>
                                {landingPage.subtitle || "Transforme sua forma de trabalhar com nossa plataforma inovadora"}
                            </Typography>
                            <Box display="flex" gap={2}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={() => {
                                        if (landingPage.sourceType === 'pwa') {
                                            window.location.href = buildUrlWithUTMs(`/pwa?id_campanha=${landingPage.id}`);
                                        } else if (quizQuestions.length > 0) {
                                            window.location.href = buildUrlWithUTMs(`/lp/${landingPage.slug}/quiz`);
                                        } else {
                                            document.getElementById('lead-capture-btn')?.click();
                                        }
                                    }}
                                    style={{ backgroundColor: primaryColor, color: "white", padding: "15px 40px", borderRadius: 30, textTransform: "none", fontSize: 16, fontWeight: 600 }}
                                >
                                    {landingPage.buttonText || "Começar Gratuitamente"}
                                </Button>

                                {/* Hidden button for LeadCaptureForm */}
                                <div style={{ display: 'none' }}>
                                    <LeadCaptureForm
                                        landingPageId={landingPage.id}
                                        primaryColor={primaryColor}
                                        buttonText="Abrir"
                                        ref={(el) => {
                                            if (el) {
                                                const btn = el.querySelector('button');
                                                if (btn) btn.id = 'lead-capture-btn';
                                            }
                                        }}
                                    />
                                </div>
                            </Box>
                            <Box mt={4} display="flex" alignItems="center" gap={3}>
                                <Box display="flex" alignItems="center">
                                    {[1, 2, 3, 4, 5].map(i => <Star key={i} style={{ color: "#fbbf24", fontSize: 20 }} />)}
                                </Box>
                                <Typography variant="body2" color="textSecondary">
                                    4.9/5 de 2.000+ avaliações
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box style={{ position: "relative", borderRadius: 20, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.15)" }}>
                                <img
                                    src={getImageUrl(landingPage.heroImage) || "https://via.placeholder.com/600x400/6366f1/ffffff?text=Hero+Image"}
                                    alt="Hero"
                                    style={{ width: "100%", display: "block" }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            )}

            {/* Features/Benefits Section */}
            {benefits.length > 0 && (
                <Box style={{ backgroundColor: "#f8fafc", padding: "80px 20px" }} id="features">
                    <Container maxWidth="lg">
                        <Box textAlign="center" mb={6}>
                            <Typography variant="h3" style={{ fontWeight: 700, marginBottom: 15 }}>
                                Por que nos escolher?
                            </Typography>
                            <Typography variant="h6" color="textSecondary">
                                Recursos poderosos para impulsionar seu negócio
                            </Typography>
                        </Box>
                        <Grid container spacing={4}>
                            {benefits.slice(0, 6).map((benefit, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                    <Card elevation={0} style={{ padding: 30, borderRadius: 15, height: "100%", border: "1px solid #e2e8f0", transition: "all 0.3s" }}>
                                        <Box style={{ width: 60, height: 60, borderRadius: 12, background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
                                            <CheckCircle style={{ color: "white", fontSize: 32 }} />
                                        </Box>
                                        <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 10 }}>
                                            {benefit.title || `Recurso ${index + 1}`}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            {benefit.description || "Descrição detalhada do recurso e seus benefícios para o usuário"}
                                        </Typography>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Container>
                </Box>
            )}

            {/* How It Works */}
            {steps.length > 0 && (
                <Container maxWidth="lg" style={{ padding: "80px 20px" }} id="how-it-works">
                    <Box textAlign="center" mb={6}>
                        <Typography variant="h3" style={{ fontWeight: 700, marginBottom: 15 }}>
                            Como funciona
                        </Typography>
                        <Typography variant="h6" color="textSecondary">
                            Simples, rápido e eficiente
                        </Typography>
                    </Box>
                    <Grid container spacing={4}>
                        {steps.slice(0, 3).map((step, index) => (
                            <Grid item xs={12} md={4} key={index}>
                                <Box textAlign="center">
                                    <Box style={{ width: 80, height: 80, borderRadius: "50%", background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`, color: "white", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 25px", fontSize: 36, fontWeight: 700, boxShadow: "0 10px 30px rgba(99,102,241,0.3)" }}>
                                        {index + 1}
                                    </Box>
                                    <Typography variant="h5" style={{ fontWeight: 600, marginBottom: 15 }}>
                                        {step.title || `Passo ${index + 1}`}
                                    </Typography>
                                    <Typography variant="body1" color="textSecondary">
                                        {step.description || "Descrição do passo e como ele funciona"}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            )}

            {/* Testimonials */}
            {testimonials.length > 0 && (
                <Box style={{ backgroundColor: "#f8fafc", padding: "80px 20px" }} id="testimonials">
                    <Container maxWidth="lg">
                        <Box textAlign="center" mb={6}>
                            <Typography variant="h3" style={{ fontWeight: 700, marginBottom: 15 }}>
                                O que dizem nossos clientes
                            </Typography>
                        </Box>
                        <Grid container spacing={4}>
                            {testimonials.slice(0, 3).map((testimonial, index) => (
                                <Grid item xs={12} md={4} key={index}>
                                    <Card elevation={0} style={{ padding: 30, borderRadius: 15, border: "1px solid #e2e8f0" }}>
                                        <Box mb={2}>
                                            {[1, 2, 3, 4, 5].map(i => <Star key={i} style={{ color: "#fbbf24", fontSize: 18 }} />)}
                                        </Box>
                                        <Typography variant="body1" style={{ marginBottom: 20, lineHeight: 1.7 }}>
                                            "{testimonial.text || "Excelente serviço! Recomendo muito."}"
                                        </Typography>
                                        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                                            {testimonial.name || "Cliente Satisfeito"}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            {testimonial.role || "Empresa"}
                                        </Typography>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Container>
                </Box>
            )}

            {/* Pricing */}
            {plans.length > 0 && (
                <Container maxWidth="lg" style={{ padding: "80px 20px" }} id="pricing">
                    <Box textAlign="center" mb={6}>
                        <Typography variant="h3" style={{ fontWeight: 700, marginBottom: 15 }}>
                            Planos e Preços
                        </Typography>
                        <Typography variant="h6" color="textSecondary">
                            Escolha o plano ideal para você
                        </Typography>
                    </Box>
                    <Grid container spacing={4} justifyContent="center">
                        {plans.map((plan, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <Card elevation={plan.featured ? 8 : 0} style={{ padding: 40, borderRadius: 20, textAlign: "center", border: plan.featured ? `2px solid ${primaryColor}` : "1px solid #e2e8f0", position: "relative", transform: plan.featured ? "scale(1.05)" : "scale(1)", transition: "all 0.3s" }}>
                                    {plan.featured && (
                                        <Box style={{ position: "absolute", top: -15, left: "50%", transform: "translateX(-50%)", background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`, color: "white", padding: "6px 20px", borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
                                            MAIS POPULAR
                                        </Box>
                                    )}
                                    <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 10, color: primaryColor }}>
                                        {plan.name || "Plano"}
                                    </Typography>
                                    <Typography variant="h3" style={{ fontWeight: 700, marginBottom: 5 }}>
                                        {plan.price || "R$ 99"}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 30 }}>
                                        {plan.description || "por mês"}
                                    </Typography>
                                    <Button variant={plan.featured ? "contained" : "outlined"} fullWidth size="large" style={{ backgroundColor: plan.featured ? primaryColor : "transparent", color: plan.featured ? "white" : primaryColor, borderColor: primaryColor, padding: "12px", fontWeight: 600, borderRadius: 30, textTransform: "none" }}>
                                        {plan.buttonText || "Escolher Plano"}
                                    </Button>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            )}

            {/* CTA Section */}
            <Box style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`, color: "white", padding: "80px 20px", textAlign: "center" }}>
                <Container maxWidth="md">
                    <Typography variant="h3" style={{ fontWeight: 700, marginBottom: 20 }}>
                        Pronto para começar?
                    </Typography>
                    <Typography variant="h6" style={{ marginBottom: 40, opacity: 0.95 }}>
                        Junte-se a milhares de clientes satisfeitos hoje mesmo
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={() => {
                            if (landingPage.sourceType === 'pwa') {
                                window.location.href = buildUrlWithUTMs(`/pwa?id_campanha=${landingPage.id}`);
                            } else {
                                window.location.href = buildUrlWithUTMs(`/lp/${landingPage.slug}/quiz`);
                            }
                        }}
                        style={{ backgroundColor: "white", color: primaryColor, padding: "15px 50px", borderRadius: 30, fontSize: 18, fontWeight: 600, textTransform: "none", boxShadow: "0 10px 30px rgba(0,0,0,0.2)" }}
                    >
                        {landingPage.buttonText || "Começar Gratuitamente"}
                    </Button>
                </Container>
            </Box>

            {/* FAQ Section */}
            {faq.length > 0 && (
                <Box style={{ backgroundColor: "#f8fafc", padding: "80px 20px" }} id="faq">
                    <Container maxWidth="md">
                        <Box textAlign="center" mb={6}>
                            <Typography variant="h3" style={{ fontWeight: 700, marginBottom: 15 }}>
                                Perguntas Frequentes
                            </Typography>
                        </Box>
                        {faq.map((item, index) => (
                            <Accordion key={index} elevation={0} style={{ marginBottom: 15, border: "1px solid #e2e8f0", borderRadius: 8, overflow: 'hidden' }}>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Typography style={{ fontWeight: 600 }}>{item.question}</Typography>
                                </AccordionSummary>
                                <AccordionDetails style={{ borderTop: "1px solid #f1f5f9" }}>
                                    <Typography color="textSecondary">
                                        {item.answer}
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Container>
                </Box>
            )}

            {/* Footer */}
            <Box style={{ backgroundColor: "#1e293b", color: "white", padding: "60px 20px 30px" }}>
                <Container maxWidth="lg">
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={4}>
                            <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 15 }}>
                                {landingPage.name || "Empresa"}
                            </Typography>
                            <Typography variant="body2" style={{ opacity: 0.8, lineHeight: 1.7 }}>
                                {landingPage.description || "Transformando negócios através da tecnologia"}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Grid container spacing={4}>
                                <Grid item xs={6} sm={3}>
                                    <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 15 }}>Produto</Typography>
                                    <Typography variant="body2" style={{ opacity: 0.7, marginBottom: 8, cursor: "pointer" }} onClick={() => scrollToSection('features')}>Recursos</Typography>
                                    <Typography variant="body2" style={{ opacity: 0.7, marginBottom: 8, cursor: "pointer" }} onClick={() => scrollToSection('pricing')}>Preços</Typography>

                                    {faq.length > 0 && (
                                        <Typography variant="body2" style={{ opacity: 0.7, cursor: "pointer" }} onClick={() => scrollToSection('faq')}>FAQ</Typography>
                                    )}
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 15 }}>Empresa</Typography>
                                    <Typography variant="body2" style={{ opacity: 0.7, marginBottom: 8 }}>Sobre</Typography>
                                    <Typography variant="body2" style={{ opacity: 0.7, marginBottom: 8 }}>Blog</Typography>
                                    <Typography variant="body2" style={{ opacity: 0.7 }}>Contato</Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 15 }}>Legal</Typography>
                                    <Typography variant="body2" style={{ opacity: 0.7, marginBottom: 8 }}>Privacidade</Typography>
                                    <Typography variant="body2" style={{ opacity: 0.7, marginBottom: 8 }}>Termos</Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Box mt={6} pt={3} style={{ borderTop: "1px solid rgba(255,255,255,0.1)", textAlign: "center" }}>
                        <Typography variant="body2" style={{ opacity: 0.7 }}>
                            {landingPage.footerCopyright || "© 2025 Todos os direitos reservados"}
                        </Typography>
                    </Box>
                </Container>
            </Box>
        </div>
    );
};

export default PublicLandingPage;
